import { Component } from '@theme/component';
import { morph } from '@theme/morph';
import { requestYieldCallback, startViewTransition } from '@theme/utilities';

const PREFETCH_LIMIT = 8;

export default class FlavorSelector extends Component {
  #abortController;

  #prefetched = new Map();

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener('pointerover', this.#prefetchFromEvent);
    this.addEventListener('focusin', this.#prefetchFromEvent);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#abortController?.abort();
    this.#prefetched.clear();
  }

  async select(event) {
    const link = event.target instanceof Element ? event.target.closest('a[data-product-url]') : null;

    if (!(link instanceof HTMLAnchorElement)) return;

    if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || event.button !== 0) return;

    if (this.dataset.morphMain !== 'true') return;

    const url = link.dataset.productUrl;
    if (!url) return;

    event.preventDefault();

    if (link.getAttribute('aria-current') === 'true') return;

    this.#abortController?.abort();
    const controller = new AbortController();
    this.#abortController = controller;

    const wasExpanded = this.dataset.expanded === 'true';

    this.dataset.loading = 'true';
    this.setAttribute('aria-busy', 'true');
    link.dataset.pending = 'true';

    try {
      const responseText = await this.#load(url, controller.signal);

      if (controller.signal.aborted) return;

      const newDocument = new DOMParser().parseFromString(responseText, 'text/html');

      newDocument.querySelector('overflow-list[defer]')?.removeAttribute('defer');

      const newMain = newDocument.querySelector('main');
      const main = document.querySelector('main');

      if (!main || !newMain) throw new Error('Flavor selector: no main content found in the response');

      await startViewTransition(() => {
        morph(main, newMain);
      });

      const newTitle = newDocument.querySelector('title')?.textContent;
      if (newTitle) document.title = newTitle;

      if (wasExpanded) this.dataset.expanded = 'true';

      requestYieldCallback(() => this.#updateUrl(url));
    } catch (error) {
      if (controller.signal.aborted || (error instanceof Error && error.name === 'AbortError')) return;

      console.error(error);

      window.location.assign(url);
    } finally {
      link.removeAttribute('data-pending');

      if (this.#abortController === controller) {
        this.#abortController = undefined;
        this.removeAttribute('data-loading');
        this.removeAttribute('aria-busy');
      }
    }
  }

  expand() {
    this.dataset.expanded = 'true';

    const revealed = this.querySelector('[data-overflow="true"] a');
    if (revealed instanceof HTMLElement) revealed.focus();
  }

  #load(url, signal) {
    const prefetched = this.#prefetched.get(url);
    if (prefetched) return prefetched;

    return fetch(url, { signal }).then(readResponse);
  }

  #prefetchFromEvent = (event) => {
    if (this.dataset.morphMain !== 'true') return;
    if (navigator.connection?.saveData) return;

    const target = event.target;
    const link = target instanceof Element ? target.closest('a[data-product-url]') : null;

    if (!(link instanceof HTMLAnchorElement) || link.getAttribute('aria-current') === 'true') return;

    const url = link.dataset.productUrl;

    if (!url || this.#prefetched.has(url) || this.#prefetched.size >= PREFETCH_LIMIT) return;

    const request = fetch(url)
      .then(readResponse)
      .catch((error) => {
        this.#prefetched.delete(url);
        throw error;
      });

    request.catch(() => {});

    this.#prefetched.set(url, request);
  };

  #updateUrl(url) {
    const next = new URL(url, window.location.origin);
    const current = new URL(window.location.href);

    const view = current.searchParams.get('view');
    if (view) next.searchParams.set('view', view);

    if (next.href !== current.href) history.replaceState({}, '', next.toString());
  }
}

function readResponse(response) {
  if (!response.ok) throw new Error(`Flavor selector: request failed with status ${response.status}`);

  return response.text();
}

if (!customElements.get('flavor-selector')) {
  customElements.define('flavor-selector', FlavorSelector);
}
